import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Microfrontend } from '@cian/mf-react';
import type { ICianMFUIElement } from '@cian/mf-registry/browser';

import { setAuthorizedModal } from 'shared/redux/landingSlice';
import { EAnalyticsEventType } from 'shared/types/analytics';

import { ERuntimeSettings } from 'shared/types/runtimeSettings';
import { ETelemetryEventType } from 'shared/types/telemetry';
import { IWidgetManifest } from 'shared/types/widgetManifest';
import { useApplicationContext } from 'shared/utils/applicationContext';
import { selectIsAuthorizedModalOpen } from '../../../selectors/landing/selectIsAuthorizedModalOpen';
import { selectRedirectInfoForAuth } from '../../../selectors/landing/selectRedirectInfoForAuth';
import { useTracking } from '../../../utils/tracking/useTracking';

export const LoginWidgetMicrofrontend = () => {
  const dispatch = useDispatch();
  const isAuthorizedModalOpen = useSelector(selectIsAuthorizedModalOpen);
  const { pathForRedirect, typeForRedirect, bankName, targetElementForRedirect } =
    useSelector(selectRedirectInfoForAuth);
  const {
    trackAuthorizationSuccess,
    trackRedirect,
    trackOpenPhoneAuthModal,
    trackOpenSmsAuthModal,
    trackClickOnRepeatSms,
    trackClickConfirmPhone,
    trackClickConfirmSms,
    trackAgreeToCommercial,
  } = useTracking();

  const { config, logger, telemetry } = useApplicationContext();
  const mfHostRef = useRef<ICianMFUIElement | null>(null);
  const handlersRef = useRef({
    handleUserAuth: () => {},
    handleCloseWidget: () => {},
    // oxlint-disable-next-line no-unused-vars
    handleAuthWidgetAnalytics: (_e: CustomEvent<EAnalyticsEventType>) => {},
  });

  const handleCloseWidget = () => {
    dispatch(setAuthorizedModal({ isOpen: false }));
  };

  const handleUserAuth = () => {
    trackAuthorizationSuccess();
    const bankNameForRedirect = bankName || null;
    const type = typeForRedirect || null;
    const targetElement = targetElementForRedirect || null;

    telemetry.increment(ETelemetryEventType.AuthSuccess);
    trackRedirect(type, targetElement, bankNameForRedirect);

    window.open(pathForRedirect, '_self');
  };

  const handleAuthWidgetAnalytics = (e: CustomEvent<EAnalyticsEventType>) => {
    switch (e.detail) {
      case EAnalyticsEventType.onPhoneScreenShown:
        telemetry.increment(ETelemetryEventType.onPhoneScreenShown);
        trackOpenPhoneAuthModal();
        break;
      case EAnalyticsEventType.onPhoneScreenConfirm:
        trackClickConfirmPhone();
        break;
      case EAnalyticsEventType.onCodeScreenShown:
        telemetry.increment(ETelemetryEventType.onCodeScreenShown);
        trackOpenSmsAuthModal();
        break;
      case EAnalyticsEventType.onCodeScreenConfirm:
        trackClickConfirmSms();
        break;
      case EAnalyticsEventType.onCodeScreenResend:
        trackClickOnRepeatSms();
        break;
      case EAnalyticsEventType.onAdvertisingConsent:
        trackAgreeToCommercial();
        break;
    }
  };

  handlersRef.current.handleUserAuth = handleUserAuth;
  handlersRef.current.handleCloseWidget = handleCloseWidget;
  handlersRef.current.handleAuthWidgetAnalytics = handleAuthWidgetAnalytics;

  const onUserAuthEvent = useCallback(() => {
    handlersRef.current.handleUserAuth();
  }, []);

  const onCloseEvent = useCallback(() => {
    handlersRef.current.handleCloseWidget();
  }, []);

  const onAnalyticsEvent = useCallback((e: Event) => {
    handlersRef.current.handleAuthWidgetAnalytics(e as CustomEvent<EAnalyticsEventType>);
  }, []);

  const setMicrofrontendHostRef = useCallback(
    (node: ICianMFUIElement | null) => {
      const prev = mfHostRef.current;
      if (prev) {
        prev.removeEventListener('user-auth', onUserAuthEvent);
        prev.removeEventListener('close', onCloseEvent);
      }
      mfHostRef.current = node;
      if (node) {
        node.addEventListener('user-auth', onUserAuthEvent);
        node.addEventListener('close', onCloseEvent);
      }
    },
    [onUserAuthEvent, onCloseEvent],
  );

  useEffect(() => {
    document.addEventListener('analytics', onAnalyticsEvent);

    return () => document.removeEventListener('analytics', onAnalyticsEvent);
  }, [onAnalyticsEvent]);

  const manifest = config.get<IWidgetManifest>(ERuntimeSettings.loginWidgetManifest);

  if (!manifest) {
    logger.error('Отсутствует манифест виджета авторизации', {
      domain: 'microfrontends.login-widget-b2c.manifest',
    });

    return null;
  }

  return (
    <Microfrontend ref={setMicrofrontendHostRef} {...manifest} parameters={{ isWidgetOpen: isAuthorizedModalOpen }} />
  );
};
